import { fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { getAdvices, isAdvicePending } from 'works/utils';
import { getEntityId } from 'modules/entity/selectors';
import { ADVICE_STATUS } from 'modules/entity/constants';
import * as websocketActions from 'modules/websockets/actionTypes';
import { openInterventionUpdateModal } from 'modules/works/actions';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import { getNewAdvicesAnchor } from 'modules/entity/subEntities/intervention/selectors/works';
import { FETCH_ENTITY, FETCH_ENTITY_SUCCESS } from 'modules/entity/actionTypes';
import { INTERVENTION_UPDATE_MODAL_TYPES } from 'works/components/Content/components/Common/modalUtils';

import { getData } from './selectors/updateModal';
import { getInterventionAdvicesIds } from './selectors/advices';

/**
 * Display new advices modal only at start of page
 *  - If we don't have localstorage at all, we add advices to it
 *  - If we have localstorage, we compare current with advices, if it's not the same we render modal
 *  - Only when we click on close modal, we overwrite the localstorage
 *  - If user refreshes the page, while he didn't click on the button, he'll be have the modal
 */
function* triggerNewAdvicesModal() {
  const { payload: intervention } = yield take(FETCH_ENTITY_SUCCESS);
  const advices = getAdvices(intervention);
  const storageIds = getLocalStorageItem(intervention.id);
  const adviceIds = advices.map(({ adviceId }) => adviceId);
  if (!storageIds) {
    setLocalStorageItem(intervention.id, adviceIds);
    return;
  }

  const wasAnswered = advices.some(({ status }) => status !== ADVICE_STATUS.PENDING);
  const hasNewAdvices = advices.some(isAdvicePending);
  if (wasAnswered && hasNewAdvices && adviceIds.some((id) => !storageIds.includes(id))) {
    yield put(openInterventionUpdateModal({ type: INTERVENTION_UPDATE_MODAL_TYPES.NEW_ADVICES }));
  }
}

function* updateIntervention({ payload }) {
  const shouldOpen = Object.values(INTERVENTION_UPDATE_MODAL_TYPES).includes(payload.type);
  if (shouldOpen) {
    yield put(openInterventionUpdateModal(payload));
  }
}

function* closeInterventionUpdateModal({ meta }) {
  if (meta.refresh) {
    const socketData = yield select(getData);
    if (socketData?.type === INTERVENTION_UPDATE_MODAL_TYPES.NEW_ADVICES) {
      const id = yield select(getEntityId);
      const adviceIds = yield select(getInterventionAdvicesIds);
      const anchor = yield select(getNewAdvicesAnchor);
      setLocalStorageItem(id, adviceIds);
      document.getElementById(anchor)?.scrollIntoView();
    } else if (socketData?.type === INTERVENTION_UPDATE_MODAL_TYPES.UPDATE && socketData.updatedFinalInvoice) {
      document.getElementById('final-invoice-card')?.scrollIntoView();
    }
  }
}

export default function* worksSagas() {
  yield fork(triggerNewAdvicesModal);
  yield takeLatest(websocketActions.INTERVENTION_UPDATE_FROM_SOCKET, updateIntervention);
  yield takeLatest(FETCH_ENTITY, closeInterventionUpdateModal);
}
